import { useEffect } from 'react'
import { Link } from 'react-router-dom'

import { resolveLegalDocument, type LegalDocumentId } from '@/components/legal/legal-content'
import {
  isAbsoluteHttpUrl,
  legalDocumentHref,
  redirectBrowserTo,
} from '@/components/legal/legal-href'
import { CURRENT_CLIENT } from '@/constants/index'
import { getSignupLegalConfig, type SignupLegalConfig } from '@/constants/signup'
import { LandingCtaLink } from '@/landing/landing-cta-link'

type LegalDocumentPageProps = {
  documentId: LegalDocumentId
  /** Omit to read this tenant's `signup.legal`. Tests pass a slice directly. */
  legal?: SignupLegalConfig | null
}

function ExternalLegalRedirect({ href }: { href: string }) {
  redirectBrowserTo(href)
  useEffect(() => {
    redirectBrowserTo(href)
  }, [href])

  return (
    <div className="min-h-full bg-page-background px-6 py-10 text-page-foreground">
      <p className="text-sm text-muted-foreground">
        Continue to{' '}
        <a href={href} rel="noopener noreferrer">
          {href}
        </a>
      </p>
    </div>
  )
}

export function LegalDocumentPage({ documentId, legal }: LegalDocumentPageProps) {
  const resolved = legal === undefined ? getSignupLegalConfig() : legal
  const href = legalDocumentHref(documentId, resolved)
  if (isAbsoluteHttpUrl(href)) {
    return <ExternalLegalRedirect href={href} />
  }

  const brand = CURRENT_CLIENT.brand || CURRENT_CLIENT.name || 'Yes Human'
  const doc = resolveLegalDocument(documentId, brand, resolved?.documents)
  const termsHref = legalDocumentHref('terms', resolved)
  const privacyHref = legalDocumentHref('privacy', resolved)

  return (
    <div className="min-h-full bg-page-background text-page-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-6 py-4">
          <Link to="/" className="text-sm font-semibold text-primary hover:underline">
            {brand}
          </Link>
          <nav className="flex items-center gap-3 text-sm text-muted-foreground">
            <LandingCtaLink to={termsHref} className="hover:text-foreground hover:underline">
              Terms
            </LandingCtaLink>
            <LandingCtaLink to={privacyHref} className="hover:text-foreground hover:underline">
              Privacy
            </LandingCtaLink>
            <Link to="/login" className="hover:text-foreground hover:underline">
              Sign in
            </Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="text-3xl font-semibold tracking-tight">{doc.title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: {doc.updated}</p>
        <p className="mt-6 text-base leading-relaxed text-muted-foreground">{doc.intro}</p>

        <div className="mt-10 space-y-8">
          {doc.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-lg font-semibold">{section.heading}</h2>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">{section.body}</p>
            </section>
          ))}
        </div>
      </main>
    </div>
  )
}
